<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import BrandLogo from "@/components/BrandLogo/index.vue";
import { useContact } from "@/hooks/useContact";

const { t } = useI18n();
const { openContact } = useContact();

const currentYear = new Date().getFullYear();

const footerGroups = computed(() => [
  {
    title: t("footer.groups.product.title"),
    links: [
      { label: t("nav.solutions"), to: "/solutions" },
      { label: t("nav.cases"), to: "/case" },
      { label: t("nav.fde"), to: "/fde" },
    ],
  },
  {
    title: t("footer.groups.learn.title"),
    links: [
      { label: t("nav.courses"), to: "/courses" },
      { label: t("nav.community"), to: "/community" },
      { label: t("nav.about"), to: "/about" },
    ],
  },
]);
</script>

<template>
  <footer class="marketing-footer norem">
    <div class="marketing-footer-inner">
      <div class="marketing-footer-main">
        <section class="marketing-footer-brand" :aria-labelledby="'footer-brand-title'">
          <RouterLink to="/" class="marketing-footer-brand-link">
            <BrandLogo variant="header" alt="Letoo AI 乐兔AI" />
          </RouterLink>
          <h2 id="footer-brand-title" class="sr-only">{{ t("brand.name") }}</h2>
          <p class="marketing-footer-description">{{ t("footer.description") }}</p>
          <button type="button" class="marketing-footer-cta" @click="openContact">
            <span>{{ t("footer.contact") }}</span>
            <span aria-hidden="true">↗</span>
          </button>
        </section>

        <nav
          v-for="group in footerGroups"
          :key="group.title"
          class="marketing-footer-nav"
          :aria-label="group.title"
        >
          <h2 class="marketing-footer-heading">{{ group.title }}</h2>
          <ul class="marketing-footer-links">
            <li v-for="link in group.links" :key="link.to">
              <RouterLink :to="link.to">{{ link.label }}</RouterLink>
            </li>
          </ul>
        </nav>

        <section class="marketing-footer-ecosystem" :aria-labelledby="'footer-ecosystem-title'">
          <h2 id="footer-ecosystem-title" class="marketing-footer-heading">
            {{ t("footer.ecosystem.title") }}
          </h2>
          <p>{{ t("footer.ecosystem.description") }}</p>
          <span class="marketing-footer-badge">{{ t("footer.ecosystem.badge") }}</span>
        </section>
      </div>

      <div class="marketing-footer-bottom">
        <p>{{ t("footer.copyright", { year: currentYear, brand: t("brand.name") }) }}</p>
        <div class="marketing-footer-bottom-links">
          <a
            class="marketing-footer-bottom-note"
            href="https://beian.miit.gov.cn"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t("footer.bottomNote") }}
          </a>
          <a
            class="marketing-footer-bottom-note"
            href="https://beian.mps.gov.cn/#/query/webSearch?code=51019002010054"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t("footer.publicSecurityFiling") }}
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.marketing-footer.norem {
  width: 100%;
  background: var(--marketing-bg-deep);
  color: #fff;
  font-family: var(--marketing-font);
}

.marketing-footer-inner {
  width: min(100%, var(--marketing-page-max));
  margin: 0 auto;
  padding: 80px var(--marketing-gutter) 28px;
}

.marketing-footer-main {
  display: grid;
  grid-template-columns: minmax(260px, 1.6fr) repeat(2, minmax(130px, 0.7fr)) minmax(220px, 1.1fr);
  gap: 48px;
  padding-bottom: 72px;
}

.marketing-footer-brand {
  min-width: 0;
}

.marketing-footer-brand-link {
  display: inline-flex;
  width: fit-content;
  margin-bottom: 28px;
}

.marketing-footer-brand-link :deep(.brand-logo) {
  max-width: 196px;
}

.marketing-footer-brand-link :deep(.brand-logo-icon) {
  height: 34px;
}

.marketing-footer-brand-link :deep(.brand-logo-wordmark) {
  height: 38px;
}

.marketing-footer-description,
.marketing-footer-ecosystem p {
  max-width: 360px;
  margin: 0;
  color: var(--marketing-text-on-dark);
  font-size: 15px;
  line-height: 1.85;
}

.marketing-footer-cta {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: color 180ms ease, transform 180ms ease;
}

.marketing-footer-cta span:last-child {
  color: var(--marketing-accent);
  font-size: 20px;
  line-height: 1;
}

.marketing-footer-cta:hover {
  color: var(--marketing-accent);
  transform: translateX(3px);
}

.marketing-footer-nav,
.marketing-footer-ecosystem {
  min-width: 0;
}

.marketing-footer-heading {
  margin: 4px 0 22px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: 1.3;
}

.marketing-footer-links {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.marketing-footer-links a {
  color: var(--marketing-text-on-dark);
  font-size: 14px;
  line-height: 1.5;
  text-decoration: none;
  transition: color 180ms ease;
}

.marketing-footer-links a:hover,
.marketing-footer-links a.router-link-active {
  color: #fff;
}

.marketing-footer-ecosystem p {
  max-width: 260px;
  font-size: 14px;
  line-height: 1.75;
}

.marketing-footer-badge {
  display: inline-flex;
  margin-top: 22px;
  padding: 8px 12px;
  border: 1px solid var(--marketing-border-dark);
  border-radius: 999px;
  color: var(--marketing-accent-purple);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.marketing-footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--marketing-border-dark);
  color: var(--marketing-ink-muted);
  font-size: 13px;
  line-height: 1.5;
}

.marketing-footer-bottom p {
  margin: 0;
}

.marketing-footer-bottom-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px 20px;
}

.marketing-footer-bottom-note {
  color: var(--marketing-text-on-dark);
  text-align: right;
  text-decoration: none;
  transition: color 180ms ease;
}

.marketing-footer-bottom-note:hover {
  color: var(--marketing-accent);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1023px) {
  .marketing-footer-main {
    grid-template-columns: minmax(240px, 1.3fr) repeat(2, minmax(120px, 0.7fr));
  }

  .marketing-footer-ecosystem {
    grid-column: 2 / -1;
  }
}

@media (max-width: 767px) {
  .marketing-footer-description,
  .marketing-footer-ecosystem p,
  .marketing-footer-links a,
  .marketing-footer-cta,
  .marketing-footer-bottom {
    font-size: var(--marketing-type-mobile-body);
  }

  .marketing-footer-heading {
    font-size: var(--marketing-type-mobile-label);
  }

  .marketing-footer-badge {
    font-size: var(--marketing-type-mobile-label);
  }

  .marketing-footer-inner {
    padding: 56px 24px 24px;
  }

  .marketing-footer-main {
    display: flex;
    flex-direction: column;
    gap: 40px;
    padding-bottom: 48px;
  }

  .marketing-footer-brand-link {
    margin-bottom: 22px;
  }

  .marketing-footer-description,
  .marketing-footer-ecosystem p {
    max-width: 100%;
  }

  .marketing-footer-heading {
    margin-bottom: 16px;
  }

  .marketing-footer-ecosystem {
    padding-top: 4px;
  }

  .marketing-footer-bottom {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .marketing-footer-bottom-links {
    justify-content: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .marketing-footer-bottom-note {
    text-align: left;
  }
}
</style>
